import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CheckIcon, ChevronDownIcon, FolderIcon, SearchIcon } from '@/components/Icons'
import { MEDIA_LG_UP, useMediaQuery } from '@/hooks/useMediaQuery'
import { useUiStrings } from '@/hooks/useUiStrings'
import type { Project } from '@/services/meetingBrief'

interface ProjectDropdownProps {
  projects: Project[]
  selected: Project | null
  /** Highlighted first, with the "suggested from the meeting name" badge. */
  suggested: Project | null
  onSelect: (project: Project) => void
  disabled?: boolean
  /**
   * The dialog element the floating panel is portalled into.
   *
   * Portalled into the dialog rather than `document.body` on purpose: the
   * dialog carries `aria-modal` and the focus trap, and a panel outside it
   * would be invisible to assistive technology even though it is on screen.
   */
  portalTarget?: HTMLElement | null
}

const PANEL_GAP = 8
const PANEL_MARGIN = 12
const PANEL_MAX_HEIGHT = 360

/**
 * The brief's project picker.
 *
 * A searchable panel rather than a native `<select>`: project keys are what
 * people quote to each other, and typing three letters of one is faster than
 * scrolling a list that will only grow.
 *
 * On desktop the panel floats over the empty state, anchored to the trigger and
 * portalled into the dialog so it escapes the modal body's scroll container.
 * Below the desktop breakpoint it sits in the flow instead: a 300px floating
 * card on a phone would cover the very empty state it is meant to explain and
 * fight the body's scroll.
 */
export default function ProjectDropdown({
  projects,
  selected,
  suggested,
  onSelect,
  disabled = false,
  portalTarget = null,
}: ProjectDropdownProps) {
  const { t } = useUiStrings()
  const isWide = useMediaQuery(MEDIA_LG_UP)
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const listId = useId()
  /**
   * Focus the search box when the panel mounts.
   *
   * A callback ref rather than an `open` effect on purpose: on desktop the
   * panel only exists after the layout effect has measured the trigger, so the
   * first `open` effect would run before there is an input to focus.
   */
  const focusSearch = useCallback((node: HTMLInputElement | null) => {
    node?.focus()
  }, [])

  const filtered = useMemo(() => {
    const query = search.trim().toLocaleLowerCase()
    if (!query) return projects
    return projects.filter((project) =>
      `${project.projectKey} ${project.name} ${project.client}`.toLocaleLowerCase().includes(query)
    )
  }, [projects, search])

  const canFloat = isWide && Boolean(portalTarget)

  const updatePanelPosition = useCallback(() => {
    if (!open || !canFloat) return
    const trigger = triggerRef.current
    if (!trigger) return

    const rect = trigger.getBoundingClientRect()
    if (rect.bottom < 0 || rect.top > window.innerHeight) {
      setOpen(false)
      return
    }

    const spaceBelow = window.innerHeight - rect.bottom - PANEL_GAP - PANEL_MARGIN
    const spaceAbove = rect.top - PANEL_GAP - PANEL_MARGIN
    // The panel needs roughly this much room before flipping; below that it is
    // more useful above the trigger than as a two-row scroll area below it.
    const openAbove = spaceBelow < 240 && spaceAbove > spaceBelow
    const maxHeight = Math.max(160, Math.min(PANEL_MAX_HEIGHT, openAbove ? spaceAbove : spaceBelow))
    const width = Math.min(rect.width, window.innerWidth - PANEL_MARGIN * 2)
    const left = Math.min(
      Math.max(PANEL_MARGIN, rect.left),
      window.innerWidth - width - PANEL_MARGIN
    )

    setPanelStyle({
      left,
      width,
      maxHeight,
      ...(openAbove
        ? { bottom: window.innerHeight - rect.top + PANEL_GAP }
        : { top: rect.bottom + PANEL_GAP }),
    })
  }, [canFloat, open])

  useLayoutEffect(() => {
    if (!open || !canFloat) {
      setPanelStyle(null)
      return
    }
    updatePanelPosition()
  }, [canFloat, open, updatePanelPosition])

  useEffect(() => {
    if (!open || !canFloat) return undefined
    const reposition = () => updatePanelPosition()
    window.addEventListener('resize', reposition)
    // Capture catches the modal body's own scroll, not just the window's.
    window.addEventListener('scroll', reposition, true)
    return () => {
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [canFloat, open, updatePanelPosition])

  useEffect(() => {
    if (!open) return
    const preferred = filtered.findIndex(
      (project) => project.projectKey === suggested?.projectKey
    )
    setActiveIndex(preferred >= 0 ? preferred : 0)
  }, [filtered, open, suggested?.projectKey])

  useEffect(() => {
    if (!open) return undefined
    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node
      if (containerRef.current?.contains(target) || panelRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  function choose(project: Project) {
    onSelect(project)
    setOpen(false)
    setSearch('')
    triggerRef.current?.focus()
  }

  function onSearchKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((index) => (filtered.length ? (index + 1) % filtered.length : 0))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((index) =>
        filtered.length ? (index - 1 + filtered.length) % filtered.length : 0
      )
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const project = filtered[activeIndex]
      if (project) choose(project)
    } else if (event.key === 'Escape') {
      // Closing the panel must not also close the modal behind it; the modal's
      // Escape handler is on `document`, so stop the event before it gets there.
      event.preventDefault()
      event.stopPropagation()
      setOpen(false)
      triggerRef.current?.focus()
    } else if (event.key === 'Tab') {
      setOpen(false)
    }
  }

  const optionId = (project: Project) => `${listId}-${project.projectKey}`
  const activeProject = filtered[activeIndex]
  // With nothing chosen yet, the check marks the suggestion, matching the
  // mock's "this is the default" reading. Once a project is chosen, the check
  // belongs to the chosen one and the suggestion keeps only its green label.
  const checkFor = (project: Project) =>
    project.projectKey === selected?.projectKey ||
    (selected === null && suggested !== null && project.projectKey === suggested.projectKey)

  const searchBox = (
    <div className="relative">
      <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--text-tertiary)]" />
      <input
        ref={focusSearch}
        type="search"
        role="combobox"
        aria-expanded
        aria-controls={listId}
        aria-activedescendant={activeProject ? optionId(activeProject) : undefined}
        aria-autocomplete="list"
        aria-label={t.briefProjectSearch}
        placeholder={t.briefProjectSearch}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        onKeyDown={onSearchKeyDown}
        className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] py-1.5 pl-8 pr-3 text-xs text-[var(--text-primary)] outline-none transition-colors placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent)] focus:ring-1 focus:ring-[var(--ring)]"
      />
    </div>
  )

  const options = (
    <ul
      id={listId}
      role="listbox"
      aria-label={t.briefProjectLabel}
      className={canFloat ? 'mt-2 space-y-1' : 'mt-1.5 max-h-64 space-y-1 overflow-y-auto'}
    >
      {filtered.length === 0 && (
        <li className="px-2 py-3 text-center text-[11px] text-[var(--text-tertiary)]">
          {t.briefProjectNoResults}
        </li>
      )}

      {filtered.map((project, index) => {
        const isSuggested = suggested !== null && project.projectKey === suggested.projectKey
        const isSelected = selected?.projectKey === project.projectKey
        const isActive = index === activeIndex
        const showCheck = checkFor(project)

        return (
          <li
            key={project.projectKey}
            id={optionId(project)}
            role="option"
            aria-selected={isSelected}
            onMouseEnter={() => setActiveIndex(index)}
            onClick={() => choose(project)}
            className={[
              'flex cursor-pointer items-center justify-between gap-3 rounded-lg px-2 py-2 transition-colors',
              isActive
                ? 'bg-[var(--accent-light)] ring-1 ring-inset ring-indigo-200/70 dark:ring-indigo-500/30'
                : isSuggested
                  ? 'bg-[var(--accent-light)]'
                  : '',
            ].join(' ')}
          >
            <span className="flex min-w-0 items-center gap-2.5">
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded ${
                  isSuggested
                    ? 'bg-indigo-100/70 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-300'
                    : 'bg-indigo-50 text-indigo-500 dark:bg-indigo-500/10 dark:text-indigo-300'
                }`}
              >
                <FolderIcon className="h-3.5 w-3.5" />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-xs font-bold leading-tight text-[var(--text-primary)]">
                  {project.name}
                </span>
                <span className="block truncate text-[11px] leading-tight text-[var(--text-secondary)]">
                  {project.client}
                </span>
              </span>
            </span>

            <span className="flex shrink-0 items-center gap-1.5 pl-2">
              {isSuggested && (
                <span className="max-w-[10rem] truncate text-[10px] font-medium text-emerald-600 dark:text-emerald-400 sm:text-[11px]">
                  {t.briefProjectSuggested}
                </span>
              )}
              {showCheck && (
                <span
                  data-testid={`brief-project-check-${project.projectKey}`}
                  aria-hidden="true"
                  className="flex items-center"
                >
                  <CheckIcon className="h-4 w-4 text-[var(--accent)]" />
                </span>
              )}
            </span>
          </li>
        )
      })}
    </ul>
  )

  const panelBody = (
    <>
      {searchBox}
      {options}
    </>
  )

  return (
    <div ref={containerRef} className="relative">
      <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-[var(--text-secondary)]">
        {t.briefProjectLabel}
      </p>

      <button
        ref={triggerRef}
        id={`${listId}-trigger`}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={`${t.briefProjectLabel}: ${
          selected ? selected.name : t.briefProjectPlaceholder
        }`}
        onClick={() => (open ? setOpen(false) : setOpen(true))}
        className={[
          'flex w-full items-center justify-between gap-2 rounded-xl bg-[var(--bg-secondary)] px-3.5 py-2.5 text-left text-xs font-medium shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] disabled:cursor-not-allowed disabled:opacity-60',
          open
            ? 'border-2 border-indigo-500 dark:border-indigo-400'
            : 'border border-indigo-400 hover:border-indigo-500 dark:border-indigo-500/70',
        ].join(' ')}
      >
        <span className="flex min-w-0 items-center gap-2">
          <FolderIcon
            className={`h-4 w-4 shrink-0 ${
              selected ? 'text-[var(--accent)]' : 'text-[var(--text-tertiary)]'
            }`}
          />
          <span className={`truncate ${selected ? '' : 'text-[var(--text-secondary)]'}`}>
            {selected ? selected.name : t.briefProjectPlaceholder}
          </span>
        </span>
        <ChevronDownIcon
          className={`h-4 w-4 shrink-0 text-[var(--text-tertiary)] transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && canFloat && panelStyle
        ? createPortal(
            <div
              ref={panelRef}
              style={panelStyle}
              data-testid="brief-project-panel"
              className="fixed z-[60] overflow-y-auto rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-2.5 shadow-xl"
            >
              {panelBody}
            </div>,
            portalTarget as HTMLElement
          )
        : null}

      {open && !canFloat && (
        <div
          ref={panelRef}
          data-testid="brief-project-panel"
          className="mt-2 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-2.5 shadow-xl"
        >
          {panelBody}
        </div>
      )}
    </div>
  )
}
